/**
 * Alert Definitions Configuration
 *
 * This file defines all alert types for the Training Matrix V2 notification system.
 * Alerts are role-based notifications configured by Admins and sent to Admins/Managers.
 *
 * @see docs/notifications-groups-v2.md for complete specification
 * @see docs/notifications-system-v2.md for system architecture
 */

/**
 * Alert Definitions by Tool
 *
 * Structure:
 * {
 *   [toolId]: {
 *     id: string,
 *     name: string,
 *     icon: string,
 *     description: string,
 *     alerts: {
 *       [alertId]: {
 *         id: string,
 *         label: string,
 *         description: string,
 *         defaults: {
 *           Admin: { app: boolean, email: boolean },
 *           Manager: { app: boolean, email: boolean }
 *         }
 *       }
 *     }
 *   }
 * }
 */
export const ALERT_DEFINITIONS = {
  bookings: {
    id: 'bookings',
    name: 'Bookings',
    icon: 'Calendar',
    description: 'Training session bookings and resource scheduling',
    alerts: {
      new_booking_created: {
        id: 'new_booking_created',
        label: 'New booking created',
        description: 'When someone creates a new booking',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      booking_status_changed: {
        id: 'booking_status_changed',
        label: 'Booking status changed',
        description: 'When a booking status is updated',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      booking_cancelled: {
        id: 'booking_cancelled',
        label: 'Booking cancelled',
        description: 'When a booking is cancelled',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      booking_reminder: {
        id: 'booking_reminder',
        label: 'Booking reminder (24h before)',
        description: 'Reminder 24 hours before booking starts',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      booking_updated: {
        id: 'booking_updated',
        label: 'Booking details updated',
        description: 'When booking date, time, or location changes',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      booking_comment_added: {
        id: 'booking_comment_added',
        label: 'New comment on booking',
        description: 'When someone comments on a booking',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      }
    }
  },

  checklists: {
    id: 'checklists',
    name: 'Checklists',
    icon: 'CheckSquare',
    description: 'Task lists for training completion and compliance',
    alerts: {
      checklist_created: {
        id: 'checklist_created',
        label: 'New checklist created',
        description: 'When a new checklist is created',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      checklist_assigned: {
        id: 'checklist_assigned',
        label: 'Checklist assigned',
        description: 'When a checklist is assigned to someone',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      checklist_completed: {
        id: 'checklist_completed',
        label: 'Checklist completed',
        description: 'When a checklist is marked complete',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      checklist_overdue: {
        id: 'checklist_overdue',
        label: 'Checklist overdue',
        description: 'When a checklist passes its due date',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      checklist_item_completed: {
        id: 'checklist_item_completed',
        label: 'Checklist item checked',
        description: 'When an item is completed',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: false, email: false }
        }
      },
      checklist_comment_added: {
        id: 'checklist_comment_added',
        label: 'New comment on checklist',
        description: 'When someone comments on a checklist',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      }
    }
  },

  checkins: {
    id: 'checkins',
    name: 'Check-ins',
    icon: 'MapPin',
    description: 'Site check-in/check-out tracking with QR codes',
    alerts: {
      missed_checkin: {
        id: 'missed_checkin',
        label: 'Missed check-in',
        description: 'When a required check-in is missed',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      unusual_checkin_location: {
        id: 'unusual_checkin_location',
        label: 'Unusual check-in location',
        description: 'When check-in occurs at unexpected location',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      late_checkin: {
        id: 'late_checkin',
        label: 'Late check-in',
        description: 'When check-in occurs after scheduled time',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      missed_checkout: {
        id: 'missed_checkout',
        label: 'Missed check-out',
        description: 'When user doesn\'t check out',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      multiple_checkins: {
        id: 'multiple_checkins',
        label: 'Multiple check-ins detected',
        description: 'When user checks into multiple sites simultaneously',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      }
    }
  },

  qualifications: {
    id: 'qualifications',
    name: 'Qualifications',
    icon: 'Award',
    description: 'Training requirements and certification management',
    alerts: {
      cert_expiring_30d: {
        id: 'cert_expiring_30d',
        label: 'Certificate expiring (30 days)',
        description: 'When a certification will expire in 30 days',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      cert_expiring_7d: {
        id: 'cert_expiring_7d',
        label: 'Certificate expiring (7 days)',
        description: 'When a certification will expire in 7 days',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      cert_expired: {
        id: 'cert_expired',
        label: 'Certificate expired',
        description: 'When a certification has expired',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      new_requirement_assigned: {
        id: 'new_requirement_assigned',
        label: 'New requirement assigned',
        description: 'When a new training requirement is assigned',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      requirement_completed: {
        id: 'requirement_completed',
        label: 'Requirement completed',
        description: 'When a team member completes a requirement',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      evidence_uploaded: {
        id: 'evidence_uploaded',
        label: 'Evidence uploaded',
        description: 'When evidence is uploaded for a requirement',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: false, email: false }
        }
      },
      bulk_expiry_warning: {
        id: 'bulk_expiry_warning',
        label: 'Multiple certifications expiring',
        description: 'When multiple certs are expiring soon for one person',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      }
    }
  },

  team: {
    id: 'team',
    name: 'Team',
    icon: 'Users',
    description: 'Team member management and role assignment',
    alerts: {
      member_added: {
        id: 'member_added',
        label: 'Team member added',
        description: 'When a new member joins a space',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      member_removed: {
        id: 'member_removed',
        label: 'Team member removed',
        description: 'When a member is removed from a space',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      role_changed: {
        id: 'role_changed',
        label: 'Company role changed',
        description: 'When a user\'s company role changes',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: false, email: false }
        }
      },
      member_profile_updated: {
        id: 'member_profile_updated',
        label: 'Team member profile updated',
        description: 'When a team member updates their profile',
        defaults: {
          Admin: { app: false, email: false },
          Manager: { app: false, email: false }
        }
      },
      bulk_team_import: {
        id: 'bulk_team_import',
        label: 'Bulk team members imported',
        description: 'When multiple team members are imported',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: false, email: false }
        }
      }
    }
  },

  compliance: {
    id: 'compliance',
    name: 'AI Compliance',
    icon: 'Shield',
    description: 'AI-powered compliance tracking and risk detection',
    alerts: {
      compliance_risk_detected: {
        id: 'compliance_risk_detected',
        label: 'Compliance risk detected',
        description: 'When AI detects a compliance issue',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      compliance_report_ready: {
        id: 'compliance_report_ready',
        label: 'Compliance report ready',
        description: 'When a compliance report is generated',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      compliance_threshold_warning: {
        id: 'compliance_threshold_warning',
        label: 'Compliance below threshold',
        description: 'When compliance rate falls below target',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      regulation_update: {
        id: 'regulation_update',
        label: 'Regulation update detected',
        description: 'When AI detects changes in regulations',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: false, email: false }
        }
      }
    }
  },

  matrix: {
    id: 'matrix',
    name: 'Matrix',
    icon: 'Grid3x3',
    description: 'Training competency matrix management',
    alerts: {
      matrix_updated: {
        id: 'matrix_updated',
        label: 'Matrix updated',
        description: 'When the training matrix structure changes',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      skill_gap_detected: {
        id: 'skill_gap_detected',
        label: 'Skill gap detected',
        description: 'When critical skills are missing',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      competency_updated: {
        id: 'competency_updated',
        label: 'Competency level changed',
        description: 'When a team member\'s competency changes',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      matrix_compliance_low: {
        id: 'matrix_compliance_low',
        label: 'Matrix compliance low',
        description: 'When overall matrix completion is low',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      }
    }
  },

  calendar: {
    id: 'calendar',
    name: 'Calendar',
    icon: 'Calendar',
    description: 'Important dates, deadlines, and milestones',
    alerts: {
      deadline_approaching: {
        id: 'deadline_approaching',
        label: 'Deadline approaching',
        description: 'When a deadline is coming up',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      deadline_missed: {
        id: 'deadline_missed',
        label: 'Deadline missed',
        description: 'When a deadline passes',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: true }
        }
      },
      event_created: {
        id: 'event_created',
        label: 'Event created',
        description: 'When a new calendar event is created',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      event_updated: {
        id: 'event_updated',
        label: 'Event updated',
        description: 'When an event date or details change',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      }
    }
  },

  message_board: {
    id: 'message_board',
    name: 'Message Board',
    icon: 'MessageSquare',
    description: 'Announcements, updates, and threaded discussions',
    alerts: {
      announcement_posted: {
        id: 'announcement_posted',
        label: 'Announcement posted',
        description: 'When a new announcement is created',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      important_post: {
        id: 'important_post',
        label: 'Important post',
        description: 'When a post is marked as important',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      },
      post_comment: {
        id: 'post_comment',
        label: 'New comment on post',
        description: 'When someone comments on a post',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: false, email: false }
        }
      }
    }
  },

  docs: {
    id: 'docs',
    name: 'Docs & Files',
    icon: 'FileText',
    description: 'Document storage and collaboration',
    alerts: {
      document_shared: {
        id: 'document_shared',
        label: 'Document shared',
        description: 'When a document is shared with the space',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: true, email: false }
        }
      },
      document_updated: {
        id: 'document_updated',
        label: 'Document updated',
        description: 'When an existing document is modified',
        defaults: {
          Admin: { app: true, email: false },
          Manager: { app: false, email: false }
        }
      },
      important_document: {
        id: 'important_document',
        label: 'Important document added',
        description: 'When a document is marked as important',
        defaults: {
          Admin: { app: true, email: true },
          Manager: { app: true, email: false }
        }
      }
    }
  }
}

/**
 * Get list of all tools that support alerts
 * @returns {Array<string>} Array of tool IDs
 */
export function getAllTools() {
  return Object.keys(ALERT_DEFINITIONS)
}

/**
 * Get tool definition by ID
 * @param {string} toolId - Tool identifier
 * @returns {Object|null} Tool definition or null if not found
 */
export function getToolDefinition(toolId) {
  return ALERT_DEFINITIONS[toolId] || null
}

/**
 * Get all alerts for a specific tool
 * @param {string} toolId - Tool identifier
 * @returns {Object|null} Alerts object or null if tool not found
 */
export function getToolAlerts(toolId) {
  const tool = ALERT_DEFINITIONS[toolId]
  return tool ? tool.alerts : null
}

/**
 * Get alert definition
 * @param {string} toolId - Tool identifier
 * @param {string} alertId - Alert identifier
 * @returns {Object|null} Alert definition or null if not found
 */
export function getAlertDefinition(toolId, alertId) {
  const alerts = getToolAlerts(toolId)
  return alerts ? alerts[alertId] || null : null
}

/**
 * Get default alert settings for a specific tool and role
 * @param {string} toolId - Tool identifier (e.g., 'bookings')
 * @param {string} role - User role ('Admin' or 'Manager')
 * @returns {Object} Default settings object { alertId: { app: boolean, email: boolean } }
 */
export function getDefaultAlertSettings(toolId, role) {
  const tool = ALERT_DEFINITIONS[toolId]
  if (!tool) {
    throw new Error(`Unknown tool: ${toolId}`)
  }

  if (role !== 'Admin' && role !== 'Manager') {
    throw new Error(`Invalid role: ${role}. Only Admin and Manager receive alerts.`)
  }

  const settings = {}
  Object.entries(tool.alerts).forEach(([alertId, alertDef]) => {
    settings[alertId] = alertDef.defaults[role]
  })

  return settings
}

/**
 * Get default settings for all tools for a specific role
 * Used when seeding a new company's default alert settings
 * @param {string} role - User role ('Admin' or 'Manager')
 * @returns {Object} Settings object { toolId: { alertId: { app: boolean, email: boolean } } }
 */
export function getAllDefaultSettings(role) {
  if (role !== 'Admin' && role !== 'Manager') {
    throw new Error(`Invalid role: ${role}. Only Admin and Manager receive alerts.`)
  }

  const allSettings = {}
  Object.entries(ALERT_DEFINITIONS).forEach(([toolId, toolDef]) => {
    allSettings[toolId] = getDefaultAlertSettings(toolId, role)
  })

  return allSettings
}

/**
 * Seed company alert defaults
 * Creates default alert settings for a new company
 * This should be called when a company is created
 *
 * @param {Object} supabase - Supabase client instance
 * @param {string} companyId - Company UUID
 * @param {string} configuredBy - User profile UUID who is setting up the company
 * @returns {Promise<{success: boolean, error?: Error}>}
 */
export async function seedCompanyAlertDefaults(supabase, companyId, configuredBy) {
  try {
    const roles = ['Admin', 'Manager']
    const records = []

    // Create records for each tool and role
    for (const role of roles) {
      for (const toolId of getAllTools()) {
        const settings = getDefaultAlertSettings(toolId, role)
        records.push({
          company_id: companyId,
          tool_id: toolId,
          role: role,
          settings: settings,
          configured_by: configuredBy
        })
      }
    }

    // Insert all records
    const { error } = await supabase
      .from('company_alert_settings')
      .insert(records)

    if (error) {
      console.error('Error seeding company alert defaults:', error)
      return { success: false, error }
    }

    return { success: true }
  } catch (error) {
    console.error('Exception seeding company alert defaults:', error)
    return { success: false, error }
  }
}

/**
 * Validate alert exists
 * @param {string} toolId - Tool identifier
 * @param {string} alertId - Alert identifier
 * @returns {boolean} True if alert exists
 */
export function isValidAlert(toolId, alertId) {
  const alertDef = getAlertDefinition(toolId, alertId)
  return alertDef !== null
}

/**
 * Get total count of alerts across all tools
 * @returns {number} Total number of alerts
 */
export function getTotalAlertCount() {
  let count = 0
  Object.values(ALERT_DEFINITIONS).forEach(tool => {
    count += Object.keys(tool.alerts).length
  })
  return count
}

/**
 * Get statistics about alert definitions
 * @returns {Object} Statistics object
 */
export function getAlertStatistics() {
  const stats = {
    totalTools: getAllTools().length,
    totalAlerts: getTotalAlertCount(),
    alertsByTool: {},
    defaultsEnabledCount: {
      Admin: { app: 0, email: 0 },
      Manager: { app: 0, email: 0 }
    }
  }

  Object.entries(ALERT_DEFINITIONS).forEach(([toolId, toolDef]) => {
    const alertCount = Object.keys(toolDef.alerts).length
    stats.alertsByTool[toolId] = alertCount

    Object.values(toolDef.alerts).forEach(alertDef => {
      if (alertDef.defaults.Admin.app) stats.defaultsEnabledCount.Admin.app++
      if (alertDef.defaults.Admin.email) stats.defaultsEnabledCount.Admin.email++
      if (alertDef.defaults.Manager.app) stats.defaultsEnabledCount.Manager.app++
      if (alertDef.defaults.Manager.email) stats.defaultsEnabledCount.Manager.email++
    })
  })

  return stats
}
